import { TextureSlotConfig, THREE } from '@easytwin/runtime';
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArcoD,
  CollapseBox,
  EasyVComponents,
  EasyVIcon,
  ImmerFormItem,
  ProInput,
  UIGenerator,
} from '../../index';
import { MaterialPreview } from './material-preview';
import { getMaterialSchema, getMaterialUVSchema, getTextureUVSchema } from './schema';
import { MaterialEditorConfig } from './type';

interface MaterialEditorProps {
  config: MaterialEditorConfig;
  material: THREE.Material; // 用于材质实时预览
  style?: React.CSSProperties;
  onClose: () => void;
  onUpdate: (config: MaterialEditorConfig) => void;
  onConfirm: (config: MaterialEditorConfig) => void;
  nameReadOnly?: boolean;
  onNameUpdate?: (value: string) => void;
  onNameConfirm?: (value: string) => void;
}

export function MaterialEditor({
  config,
  material,
  style,
  onClose,
  onUpdate,
  onConfirm,
  nameReadOnly = true,
  onNameUpdate,
  onNameConfirm,
}: MaterialEditorProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // 材质编辑面板中UV配置模式
  const [mode, setMode] = useState<'all' | 'single'>('all');

  // 材质编辑面板中纹理槽位名称列表
  const [slotList, setSlotList] = useState<{ key: string; label: string }[]>([]);

  // 材质编辑面板中选中的纹理槽位名称
  const [selectedSlot, setSelectedSlot] = useState<string>('map');

  // 材质编辑面板中选中的纹理槽位UV配置
  const [textureSlotConfig, setTextureSlotConfig] = useState<TextureSlotConfig>();

  // 材质通用配置的Schema
  const commonSchema = useMemo(() => getMaterialSchema(config), [config]);
  // 材质UV配置的Schema
  const materialUVSchema = useMemo(() => getMaterialUVSchema(), []);
  // 纹理槽位的Schema
  const textureUVSchema = useMemo(() => getTextureUVSchema(), []);

  const hasUVConfig = material.type !== 'LineMaterial';

  useEffect(() => {
    const slots = Object.entries(config.textureSlots)
      .filter(([key, value]) => {
        return value.config?.enable;
      })
      .map(([key]) => {
        return {
          key,
          label: key,
        };
      });

    setSlotList(slots);
  }, [config]);

  // 切换选中的纹理槽时更新UV配置
  useEffect(() => {
    if (selectedSlot) {
      const textureSlot = config.textureSlots[selectedSlot as keyof typeof config.textureSlots];
      setTextureSlotConfig(textureSlot?.config);
    }
  }, [selectedSlot, config]);

  // 处理单个纹理UV配置更新
  const handleTextureUVUpdate = useCallback(
    (json: TextureSlotConfig) => {
      if (!selectedSlot) return;

      const newConfig = {
        ...config,
        textureSlots: {
          ...config.textureSlots,
          [selectedSlot]: {
            ...config.textureSlots[selectedSlot as keyof typeof config.textureSlots],
            config: json,
          },
        },
      };

      onUpdate(newConfig as MaterialEditorConfig);
    },
    [selectedSlot, config, onUpdate],
  );

  const handleTextureUVConfirm = useCallback(
    (json: any) => {
      if (!selectedSlot) return;

      const newConfig = {
        ...config,
        textureSlots: {
          ...config.textureSlots,
          [selectedSlot]: {
            ...config.textureSlots[selectedSlot as keyof typeof config.textureSlots],
            config: json,
          },
        },
      };

      onConfirm(newConfig);
    },
    [selectedSlot, config, onConfirm],
  );

  const handleClose = () => {
    onClose();
  };

  // 在函数开始处进行参数验证
  if (!config || !material)
    return (
      <div className='flex items-center justify-center p-4 text-red-500 bg-red-100 rounded-md'>
        <span>材质配置无效，请检查输入参数</span>
      </div>
    );

  return (
    <div
      className='w-80 px-3 py-3 border rounded-lg border-borderColorMain bg-bgColor232324 shadow-shadowlg'
      style={style}
      ref={panelRef}
    >
      <div className='h-6 mb-4 text-white flex justify-between items-center text-sm'>
        <span className='align-middle'>材质编辑</span>
        <EasyVIcon.CloseOutlined
          className='anticon hover:text-white text-textColorSecondary p-1 text-base cursor-pointer'
          size={14}
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        />
      </div>
      <ProInput
        disabled={nameReadOnly}
        value={config.name}
        onChange={(name: string, value: string) => {
          onNameUpdate?.(value);
        }}
        name={config.name}
        onBlur={(name: string, value: string) => {
          onNameConfirm?.(value);
        }}
      />
      <MaterialPreview material={material} />
      <ArcoD.Divider className='my-3' />
      <div className='overflow-y-auto no-scroll-container' style={{ maxHeight: '50vh' }}>
        {/* 通用配置 */}
        <UIGenerator
          comp={null as any}
          schema={commonSchema}
          json={config}
          onUpdate={(json) => {
            // 设置是否开启基础纹理
            if (json.textureSlots.map.value === undefined) {
              json.textureSlots.map.config.enable = false;
            } else {
              json.textureSlots.map.config.enable = true;
            }

            onUpdate(json);
          }}
          onConfirm={(json) => {
            // 设置是否开启基础纹理
            if (json.textureSlots.map.value === undefined) {
              json.textureSlots.map.config.enable = false;
            } else {
              json.textureSlots.map.config.enable = true;
            }

            onConfirm(json);
          }}
        />
        {/* UV配置 */}
        {hasUVConfig && (
        <CollapseBox
          type={'default'}
          title={'UV设定'}
          headerClassName='px-0'
          showConnectionLine={true}
          showChildren={false}
        >
          <ImmerFormItem label={'UV'}>
            <EasyVComponents.Radio.Group
              type={'button'}
              size={'small'}
              buttonStyle={'solid'}
              block
              value={mode}
              onChange={(value) => {
                setMode(value);
              }}
            >
              <EasyVComponents.Radio value={'all'}>{'全部'}</EasyVComponents.Radio>
              <EasyVComponents.Radio value={'single'}>{'单项'}</EasyVComponents.Radio>
            </EasyVComponents.Radio.Group>
          </ImmerFormItem>
          {mode === 'all' ? (
            // 材质UV偏移配置
            <UIGenerator
              comp={null as any}
              schema={materialUVSchema}
              json={config}
              onUpdate={onUpdate}
              onConfirm={onConfirm}
            />
          ) : (
            // 纹理UV偏移配置
            <>
              <ImmerFormItem>
                <EasyVComponents.Select
                  size='small'
                  value={selectedSlot!}
                  onChange={(value) => {
                    setSelectedSlot(value);
                  }}
                >
                  {slotList?.map((a) => (
                    <EasyVComponents.Select.Option key={a.key} value={a.key}>
                      {a.label}
                    </EasyVComponents.Select.Option>
                  ))}
                </EasyVComponents.Select>
              </ImmerFormItem>
              {selectedSlot && textureSlotConfig && (
                <UIGenerator
                  comp={null as any}
                  schema={textureUVSchema}
                  json={textureSlotConfig}
                  onUpdate={handleTextureUVUpdate}
                  onConfirm={handleTextureUVConfirm}
                />
              )}
            </>
          )}
          </CollapseBox>
        )}
      </div>
    </div>
  );
}
